import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { css } from '/imports/ui/css';
import { Icon } from '/imports/ui/Icon';

export const ModalStyled = styled.div`
  .modal-overlay {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: ${css.primaryColorTransparent};
    z-index: 100;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
  }
  .modal-window {
    margin: auto;
    background: #FFF;
    position: relative;
    padding: 2.5rem;
    box-sizing: border-box;
    max-width: 100%;
    max-height: 100%;
    overflow: auto;
    border-radius: ${css.borderRadius};
    border: ${css.border};
  }
  .modal-title {
    margin: 0;
    font-weight: normal;
  }
  .modal-body {
    margin: 1rem 0;
  }
  .modal-buttons {
    margin-top: 1rem;
    display: flex;
    gap: 1rem;
  }
  .modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    cursor: pointer;
    &:hover {
      svg {
        * {
          stroke: ${css.secondaryColor};
        }
      }
    }
  }
  /* Every modal renders in a portal, so a page's scoped .badge rule never reaches it. Declaring the base
     pill here means each modal no longer has to remember to re-declare it — TaskCardModalStyled forgot,
     which left the .doc-badge status pills square. Background comes from the Badge component's inline
     style; this only owns the shape. */
  .badge { display: inline-block; border-radius: 1rem; padding: 0.1rem 0.6rem; font-size: 0.8rem; color: ${css.white}; }
`;

export interface ModalProps {
  title?: string | React.ReactNode;
  body?: string | React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  buttons?: React.ReactNode;
  onClose?(): void;
  styled?: any;
}

// Every mounted modal, innermost last. Esc answers only the last one: the task card opens a document
// modal on top of itself, and one keypress closing both would be a surprise.
const openModals: symbol[] = [];

/** Renders a dialog with title, body, and buttons into a body-level portal. */
export const Modal = (props: ModalProps) => {
  const closeRef = useRef<HTMLSpanElement>(null);
  // Read through a ref so the listener below can register once: every call site passes a fresh arrow for
  // onClose, so depending on it directly would re-bind on every render.
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;

  /**
   * Whether this modal may be dismissed without using its buttons — true when it has a close X that is
   * both wired up and actually on screen, which is the affordance Esc and the overlay stand in for.
   * @returns true when Esc / an overlay click should close it
   */
  const dismissible = () => {
    if (!onCloseRef.current) return false;
    const el = closeRef.current;
    if (!el) return false;
    const style = getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };

  useEffect(() => {
    const id = Symbol('modal');
    openModals.push(id);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (openModals[openModals.length - 1] !== id) return;   // an inner modal is on top of this one
      if (!dismissible()) return;
      e.stopPropagation();
      onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = openModals.indexOf(id);
      if (i >= 0) openModals.splice(i, 1);
    };
  }, []);

  return createPortal(
    React.createElement(props.styled || ModalStyled, {
      className: `modal ${props.className || ''}`,
    },
    <div
      className="modal-overlay"
      // The backdrop only — a click that bubbled up from the window itself is a click on the content.
      onClick={(e) => { if (e.target === e.currentTarget && dismissible()) onCloseRef.current?.(); }}
    >
      <div className="modal-window">
        <h1 className="modal-title">
          {props.title || ''}
          <span className="modal-close" ref={closeRef} onClick={props.onClose}>
            <Icon icon="close" />
          </span>
        </h1>
        <div className="modal-body">
          {props.children || props.body || ''}
        </div>
        <div className="modal-buttons">
          {props.buttons || null}
        </div>
      </div>
    </div>
    ),
    document.body
  );
};